import { logHandledError } from '@/lib/logHandledError'
import type { EventType } from '@walter/shared'
import { EventBuffer, EventTypes } from '@walter/shared'
import axios, { getVersionForHeader } from './axios'

const API_LOG_URL = '/api/v1/log'
const API_LOG_ERROR_URL = '/api/v1/log/error'

// ---------------------------------------------------------------------------
// Client-side session ID — groups events within a single browser session.
// Stored in sessionStorage so it survives page reloads but not new tabs.
// ---------------------------------------------------------------------------

const SESSION_ID_KEY = 'w_e_sid' // w_event_sid

const getClientSessionId = (): string | undefined => {
    if (typeof window === 'undefined') return undefined
    try {
        let sid = sessionStorage.getItem(SESSION_ID_KEY)
        if (!sid) {
            sid = crypto.randomUUID()
            sessionStorage.setItem(SESSION_ID_KEY, sid)
        }
        return sid
    } catch {
        return undefined
    }
}

/** Clear the client session ID (call on logout). */
export const clearClientSessionId = () => {
    if (typeof window === 'undefined') return
    try {
        sessionStorage.removeItem(SESSION_ID_KEY)
    } catch {
        // ignore
    }
}

/** Enrich metadata with the client session ID. */
const withSessionId = (metadata?: Record<string, unknown>): Record<string, unknown> => {
    const sid = getClientSessionId()
    return { ...metadata, ...(sid ? { clientSessionId: sid } : {}) }
}

// ---------------------------------------------------------------------------
// Event buffer — collects events and flushes them as a single batch request.
// Uses shared EventBuffer from @walter/shared.
// ---------------------------------------------------------------------------

interface BufferedEvent {
    event: EventType
    referenceId: string
    metadata?: Record<string, unknown>
    status?: string
}

const eventBuffer = new EventBuffer<BufferedEvent>({
    sendBatch: (events) => axios.post(API_LOG_URL, events, { headers: { ...getVersionForHeader() } }),
    sendBeaconBatch: (events) => {
        const blob = new Blob([JSON.stringify(events)], { type: 'application/json' })
        navigator.sendBeacon(API_LOG_URL, blob)
    },
    onError: (error) => logHandledError(error, { message: 'Failed to flush event batch', section: 'eventLogging' }),
})

/** Flush buffered events immediately (e.g. before logout). */
export const flushEventBuffer = () => eventBuffer.flush()

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const logError = (error: Error, section?: string, metadata?: Record<string, unknown>) => {
    logHandledError(error, {
        message: section ? `[${section}] Error:` : 'error',
        section: 'eventLogging',
        ...metadata,
    })
}

/**
 * Queue a feature event for batched delivery.
 * Events are flushed every 2s, when the buffer hits 20 items,
 * or when the page is unloaded.
 */
export const logEventViaApi = (
    event: EventType,
    referenceId: string,
    metadata?: Record<string, unknown>,
    _status?: string,
) => {
    eventBuffer.push({
        event,
        referenceId,
        metadata: withSessionId(metadata),
    })
}

/** Error events are sent immediately (no batching). */
export const logErrorEventViaApi = async (
    event: EventType,
    referenceId: string,
    metadata?: Record<string, unknown>,
    status?: string,
) => {
    const response = await axios.post(
        API_LOG_ERROR_URL,
        {
            event,
            referenceId,
            metadata: withSessionId(metadata),
            status,
        },
        { headers: { ...getVersionForHeader() } },
    )
    return response
}

export const logClickEvent = ({
    action,
    referenceId,
    metadata,
}: {
    action: string
    referenceId?: string
    metadata?: Record<string, unknown>
}) => {
    logEventViaApi(EventTypes.Action.CLICK, referenceId ?? '', {
        ...metadata,
        action: action || 'unknown_action',
    })
}

export const logPageView = ({
    path,
    referenceId,
    metadata,
}: {
    path: string
    referenceId?: string
    metadata?: Record<string, unknown>
}) => {
    logEventViaApi(EventTypes.Action.PAGE_VIEW, referenceId ?? '', {
        ...metadata,
        path,
    })
}
